import Papa from 'papaparse';

class DataProcessor {
  constructor() {
    this.battingData = [];
    this.bowlingData = [];
    this.teamResultsData = [];
  }

  async processData() {
    try {
      // Load CSV files from results directory
      const battingResponse = await fetch('/results/player_career_stats_batting.csv');
      const bowlingResponse = await fetch('/results/player_career_stats_bowling.csv');
      const teamResultsResponse = await fetch('/results/team_results_summary.csv');

      if (!battingResponse.ok || !bowlingResponse.ok || !teamResultsResponse.ok) {
        throw new Error('Failed to load CSV files');
      }

      const battingCsv = await battingResponse.text();
      const bowlingCsv = await bowlingResponse.text();
      const teamResultsCsv = await teamResultsResponse.text();

      // Parse CSV files
      this.battingData = Papa.parse(battingCsv, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => h.trim(),
        transform: (value) => value.trim()
      }).data;

      this.bowlingData = Papa.parse(bowlingCsv, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => h.trim(),
        transform: (value) => value.trim()
      }).data;

      this.teamResultsData = Papa.parse(teamResultsCsv, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => h.trim(),
        transform: (value) => value.trim()
      }).data;

      console.log('Loaded teamResults data:', this.teamResultsData);
      console.log('First team result row:', this.teamResultsData[0]);

      // Build hierarchical structure
      const clubsData = this.buildHierarchy();
      return {
        clubs: clubsData,
        battingData: this.battingData,
        bowlingData: this.bowlingData,
        teamResults: this.teamResultsData
      };
    } catch (error) {
      console.error('Error processing data:', error);
      throw error;
    }
  }

  buildHierarchy() {
    const clubs = {};

    // Group batting data
    this.battingData.forEach((row) => {
      if (!row['Year'] || !row['Team'] || !row['Player Name']) return;

      const clubName = this.extractClubName(row['Team']);
      const year = row['Year'];
      const team = row['Team'];

      if (!clubs[clubName]) {
        clubs[clubName] = {};
      }
      if (!clubs[clubName][team]) {
        clubs[clubName][team] = {};
      }
      if (!clubs[clubName][team][year]) {
        clubs[clubName][team][year] = {
          batting: [],
          bowling: []
        };
      }

      clubs[clubName][team][year].batting.push({
        playerName: row['Player Name'],
        matches: parseInt(row['Matches']) || 0,
        innings: parseInt(row['Innings']) || 0,
        runs: parseInt(row['Runs']) || 0,
        balls: parseInt(row['Balls']) || 0,
        avg: parseFloat(row['Avg']) || 0,
        sr: parseFloat(row['SR']) || 0,
        hs: parseInt(row['HS']) || 0,
        fours: parseInt(row['Fours']) || 0,
        sixes: parseInt(row['Sixes']) || 0
      });
    });

    // Group bowling data
    this.bowlingData.forEach((row) => {
      if (!row['Year'] || !row['Team'] || !row['Player Name']) return;

      const clubName = this.extractClubName(row['Team']);
      const year = row['Year'];
      const team = row['Team'];

      if (!clubs[clubName]) {
        clubs[clubName] = {};
      }
      if (!clubs[clubName][team]) {
        clubs[clubName][team] = {};
      }
      if (!clubs[clubName][team][year]) {
        clubs[clubName][team][year] = {
          batting: [],
          bowling: []
        };
      }

      clubs[clubName][team][year].bowling.push({
        playerName: row['Player Name'],
        matches: parseInt(row['Matches']) || 0,
        overs: parseFloat(row['Overs']) || 0,
        maidens: parseInt(row['Maidens']) || 0,
        runsConceded: parseInt(row['Runs Conceded']) || 0,
        wickets: parseInt(row['Wickets']) || 0,
        avg: row['Avg'] === 'N/A' ? 'N/A' : (parseFloat(row['Avg']) || 0),
        economy: parseFloat(row['Economy']) || 0,
        bestFigures: row['Best Figures'] || '-'
      });
    });

    // Sort players within each year/team
    Object.keys(clubs).forEach((clubName) => {
      Object.keys(clubs[clubName]).forEach((team) => {
        Object.keys(clubs[clubName][team]).forEach((year) => {
          // Sort batsmen by runs (descending)
          clubs[clubName][team][year].batting.sort((a, b) => b.runs - a.runs);
          // Sort bowlers by wickets (descending)
          clubs[clubName][team][year].bowling.sort((a, b) => b.wickets - a.wickets);
        });
      });
    });

    return clubs;
  }

  extractClubName(teamName) {
    // Extract club name from team name
    // e.g., "Antwerp CC-3" -> "Antwerp CC", "BGCC-3" -> "BGCC"
    const parts = teamName.split('-');
    return parts[0].trim();
  }
}

export default DataProcessor;
